import React, { useCallback, useState } from "react";
import WidgetCard from "../_shared/WidgetCard";
import ViewAllButton from "../_shared/ViewAllButton";
import ViewAllModal from "../modals/ViewAllModal";
import { ViewAllComparisonSkeleton } from "../Skeleton";
import { CampusDatum, VIEW_ALL_PAGE_SIZE, heatCell } from "../data";
import { getCampusComparison } from "shared/services/headOfficeService";
import { mapComparison } from "../apiMappers";
import type { ViewAllPage } from "../modals/ViewAllModal";
import styles from "./style.module.scss";

/** rows the card itself renders — anything past this lives behind "View All" */
const PREVIEW_ROWS = 4;

interface CampusComparisonProps {
  cols: string[];
  campuses: CampusDatum[];
  /**
   * Total rows the endpoint reports (not just this page). `campuses` only holds
   * the first page, so its length can't answer "is there more?" — pass the
   * pagination total when it's available.
   */
  total?: number;
  /** reporting window so the modal fetches the same range */
  win: { from: string; to: string };
  /** copy overrides for campus-scoped reuse ("Class Comparison") */
  title?: string;
  subtitle?: string;
  /** override the paginated fetch (campus reuse hits the campus endpoint) */
  fetchPage?: (page: number) => Promise<ViewAllPage>;
}

/** Side-by-side campus metric heatmap. Shows the top 4; "View All" paginates the rest. */
export default function CampusComparison({
  cols,
  campuses,
  total,
  win,
  title = "Campus Comparison",
  subtitle = "Key metrics side by side",
  fetchPage: fetchPageProp,
}: CampusComparisonProps) {
  const [open, setOpen] = useState(false);

  const renderTable = (list: CampusDatum[]) => (
    <div className={styles.inner}>
      <div className={styles.headRow}>
        <div />
        {cols.map((c) => (
          <div className={styles.colLabel} key={c}>
            {c}
          </div>
        ))}
      </div>
      {list.map((campus) => {
        const vals = [
          campus.adoption,
          campus.quiz,
          campus.ageApprop,
          campus.completion,
          campus.active,
        ];
        return (
          <div className={styles.dataRow} key={campus.name}>
            <div className={styles.rowName} data-ho-blur="campus">
              {campus.name}
            </div>
            {vals.map((v, i) => {
              const h = heatCell(v);
              return (
                <div
                  className={styles.cell}
                  key={i}
                  style={{ background: h.bg, color: h.fg }}
                >
                  {v}%
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );

  const fetchPage = useCallback(
    async (page: number) => {
      const res = await getCampusComparison({ ...win, per_page: VIEW_ALL_PAGE_SIZE, page });
      const data = res?.data?.data ?? {};
      const p = data?.pagination ?? {};
      return {
        rows: mapComparison(data),
        total: Number(p.total) || 0,
        lastPage: Number(p.last_page) || 1,
        currentPage: Number(p.current_page) || page,
      };
    },
    [win.from, win.to],
  );

  const effectiveFetchPage = fetchPageProp ?? fetchPage;

  /* Prefer the server total; fall back to the rows in hand when the caller
     doesn't pass one (it fetches a bigger first page) or the API omits
     pagination — 0 would otherwise read as "nothing more to show". */
  const knownRows = total && total > 0 ? total : campuses.length;
  const hasMore = knownRows > PREVIEW_ROWS;

  return (
    <WidgetCard
      title={title}
      subtitle={subtitle}
      right={
        hasMore ? <ViewAllButton onClick={() => setOpen(true)} /> : undefined
      }
      flush
    >
      <div className={styles.scroll} data-ho-scroll>
        {renderTable(campuses.slice(0, PREVIEW_ROWS))}
      </div>

      {open && (
        <ViewAllModal
          title={title}
          subtitle={subtitle}
          onClose={() => setOpen(false)}
          fetchPage={effectiveFetchPage}
          renderContent={(rows) => renderTable(rows as CampusDatum[])}
          skeleton={
            <ViewAllComparisonSkeleton
              rows={VIEW_ALL_PAGE_SIZE}
              cols={cols.length}
            />
          }
        />
      )}
    </WidgetCard>
  );
}
